import { useEffect, useState } from "react";
import { api } from "../api";
import type { CaseVolume } from "../types";
import { formatMonthYear as formatMonthYearUtc } from "../lib/formatDate";

// Threshold-prompt banner for the volumes feature. Shown above the
// Timeline when the current open volume crosses either of the two
// thresholds (75 records OR 50K estimated tokens). Single-volume
// cases below the threshold see nothing.
//
// The banner is dismissable. Dismissal is tracked in localStorage
// keyed by volume id + the record-count at dismissal time; the banner
// reappears when the user adds another 20 records.
//
// After a successful close, the banner turns into a green success
// toast for ~10 seconds.

interface Props {
  caseId: string;
  volumes: CaseVolume[];
  onVolumeChanged: () => void; // refetch volumes + records
}

const RECORD_THRESHOLD = 75;
const TOKEN_THRESHOLD = 50_000;
const RECORDS_PER_RE_PROMPT = 20;
const SUCCESS_AUTO_DISMISS_MS = 10_000;

function dismissKey(volumeId: string): string {
  return `casethread:volume-dismiss:${volumeId}`;
}

function loadDismissedAtCount(volumeId: string): number | null {
  try {
    const raw = window.localStorage.getItem(dismissKey(volumeId));
    if (!raw) return null;
    const n = parseInt(raw, 10);
    return Number.isFinite(n) ? n : null;
  } catch {
    return null;
  }
}

function saveDismissedAtCount(volumeId: string, count: number): void {
  try {
    window.localStorage.setItem(dismissKey(volumeId), String(count));
  } catch {
    /* private mode — banner will reappear on reload, that's fine */
  }
}

export default function VolumeBanner({
  caseId,
  volumes,
  onVolumeChanged,
}: Props) {
  const [explanationOpen, setExplanationOpen] = useState(false);
  const [closing, setClosing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<{
    volumeNumber: number;
    recordCount: number;
    firstDate: string | null;
    lastDate: string | null;
  } | null>(null);
  // Reload of the dismiss marker — bumped when the user clicks Not
  // now so the suppressed banner stops rendering immediately (the
  // localStorage write alone doesn't trigger a re-render).
  const [dismissVersion, setDismissVersion] = useState(0);

  // Auto-dismiss the success toast.
  useEffect(() => {
    if (!success) return;
    const t = window.setTimeout(
      () => setSuccess(null),
      SUCCESS_AUTO_DISMISS_MS,
    );
    return () => window.clearTimeout(t);
  }, [success]);

  const openVolume = volumes.find((v) => v.closed_at === null) ?? null;

  // Show the prompt only when the open volume has crossed a threshold
  // AND the user hasn't dismissed at the same (or higher) record
  // count recently. "Within 20 records" is the cooldown.
  let showPrompt = false;
  if (openVolume && !success) {
    const crossed =
      openVolume.record_count >= RECORD_THRESHOLD ||
      openVolume.estimated_tokens >= TOKEN_THRESHOLD;
    if (crossed) {
      const dismissed = loadDismissedAtCount(openVolume.id);
      void dismissVersion; // include in closure for re-render trigger
      if (
        dismissed === null ||
        openVolume.record_count >= dismissed + RECORDS_PER_RE_PROMPT
      ) {
        showPrompt = true;
      }
    }
  }

  async function closeVolume() {
    setClosing(true);
    setError(null);
    try {
      const res = await api.closeVolume(caseId);
      setSuccess({
        volumeNumber: res.closed.volume_number,
        recordCount: res.closed.record_count,
        firstDate: res.closed.first_record_date,
        lastDate: res.closed.last_record_date,
      });
      setExplanationOpen(false);
      onVolumeChanged();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setClosing(false);
    }
  }

  function dismiss() {
    if (!openVolume) return;
    saveDismissedAtCount(openVolume.id, openVolume.record_count);
    setDismissVersion((n) => n + 1);
  }

  if (success) {
    const dateRange = formatDateRange(success.firstDate, success.lastDate);
    return (
      <div
        className="volume-banner volume-banner-success"
        role="status"
        onClick={() => setSuccess(null)}
      >
        <span className="volume-banner-icon" aria-hidden>
          ✓
        </span>
        <div className="volume-banner-body">
          <div className="volume-banner-heading">
            Volume {success.volumeNumber} closed — {success.recordCount}{" "}
            records summarised
          </div>
          <div className="volume-banner-sub">
            A summary covering {dateRange} has been saved. You're now in
            Volume {success.volumeNumber + 1}. AI features will use the
            summary of Volume {success.volumeNumber} plus the full detail
            of new records going forward.
          </div>
        </div>
      </div>
    );
  }

  if (!showPrompt || !openVolume) return null;

  const monthSpan = formatMonthSpan(
    openVolume.first_record_date,
    openVolume.last_record_date,
  );

  return (
    <div className="volume-banner volume-banner-info" role="status">
      <span className="volume-banner-icon" aria-hidden>
        ⓘ
      </span>
      <div className="volume-banner-body">
        <div className="volume-banner-heading">
          This case has {openVolume.record_count} records
          {monthSpan ? ` across ${monthSpan}` : ""}
        </div>
        <div className="volume-banner-sub">
          AI features work best with a focused set of records. Closing
          this volume saves a summary snapshot and opens a fresh chapter
          — you keep full access to everything.{" "}
          <button
            type="button"
            className="volume-banner-explain"
            onClick={() => setExplanationOpen((v) => !v)}
            aria-expanded={explanationOpen}
          >
            {explanationOpen ? "Hide explanation" : "Why does this matter?"}
          </button>
        </div>
        {explanationOpen && (
          <div className="volume-banner-explanation">
            <div className="volume-banner-point">
              <strong>1. AI has a reading limit.</strong> Every time you
              ask CaseThread a question, generate a summary, or request
              a suggested action, the AI reads your entire case from
              scratch. As the case grows, this gets slower, more
              expensive, and less accurate — the AI has to skim older
              material to fit everything in.
            </div>
            <div className="volume-banner-point">
              <strong>2. Volumes solve this.</strong> Closing a volume
              generates a detailed summary of everything so far — dates,
              amounts, commitments, who said what. New AI queries read
              that summary plus the full detail of your recent records.
              The AI stays fast and focused.
            </div>
            <div className="volume-banner-point">
              <strong>3. Nothing is lost.</strong> All your records stay
              exactly where they are — same case, same timeline, same
              exports. You can still ask the AI to look back at any
              closed volume in full detail whenever you need to.
            </div>
            <div className="volume-banner-callout">
              Think of it like chapters in a book. You wouldn't re-read
              Chapter 1 every time you open Chapter 3. The table of
              contents tells you what happened — and you can always flip
              back.
            </div>
          </div>
        )}
        {error && <div className="form-error">{error}</div>}
        <div className="volume-banner-actions">
          <button
            type="button"
            className="btn-primary"
            onClick={closeVolume}
            disabled={closing}
          >
            {closing
              ? "Summarising volume…"
              : explanationOpen
                ? "Close this volume"
                : "Close volume & start new one"}
          </button>
          <button
            type="button"
            className="btn-ghost"
            onClick={dismiss}
            disabled={closing}
          >
            {explanationOpen ? "Remind me later" : "Not now"}
          </button>
        </div>
      </div>
    </div>
  );
}

function formatDateRange(
  first: string | null,
  last: string | null,
): string {
  if (!first && !last) return "this period";
  const f = formatMonthYear(first);
  const l = formatMonthYear(last);
  if (f && l && f !== l) return `${f} – ${l}`;
  return f ?? l ?? "this period";
}

function formatMonthSpan(
  first: string | null,
  last: string | null,
): string | null {
  if (!first || !last) return null;
  try {
    const a = new Date(first);
    const b = new Date(last);
    if (Number.isNaN(a.getTime()) || Number.isNaN(b.getTime())) return null;
    const monthsDiff =
      (b.getUTCFullYear() - a.getUTCFullYear()) * 12 +
      (b.getUTCMonth() - a.getUTCMonth()) +
      1;
    if (monthsDiff <= 1) return null;
    if (monthsDiff < 12) return `${monthsDiff} months`;
    const years = Math.floor(monthsDiff / 12);
    const rem = monthsDiff % 12;
    if (rem === 0) return years === 1 ? "a year" : `${years} years`;
    return years === 1
      ? `over a year`
      : `${years} years, ${rem} month${rem === 1 ? "" : "s"}`;
  } catch {
    return null;
  }
}

// Delegates to the shared UTC-safe utility. Returns null on empty
// input to preserve the downstream "show nothing if we can't parse"
// branch used by the volume date-range caller.
function formatMonthYear(iso: string | null): string | null {
  if (!iso) return null;
  return formatMonthYearUtc(iso) || null;
}
